import { getTimeDiffAsString } from '@sparted/shared-library';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { COLORS } from 'Components/foundation';
import MModalGroup, { MModalGroupItem } from 'Components/modal/MModalGroup/MModalGroup';
import SCourseCard from 'Components/structural/SCourseCard/SCourseCard';
import UIllustration from 'Components/unit/UIllustration/UIllustration';
import { useTimeoutCallback } from 'Hooks/useTimeoutCallback/useTimeoutCallback';
import { useAppDispatch } from 'Libs/redux/store';
import Enum from 'Models/Enum';

import { actions } from '../../../redux/slice';

import styles from './CourseArchiveModal.style';

export type CourseData = {
  id: number;
  title: string;
  status: 'published' | 'pending' | 'unpublished' | 'archived';
  language: string;
  lastEditDate: Date;
  lastEditUsername: string;
  image?: string;
};

export type CourseArchiveModalProps = {
  visible: boolean;
  isArchived: boolean;
  courseData: CourseData;
  onConfirm: () => Promise<any>;
  onClose: () => void;
};

const modalStepMap = {
  ARCHIVING: 'ARCHIVE',
  UNARCHIVING: 'UNARCHIVE',
  SUCCESS: 'SUCCESS',
  ERRORS: 'ERRORS',
};

const SUCCESS_MODAL_TIMEOUT = 1500;

export const CourseArchiveModal: React.FC<CourseArchiveModalProps> = ({
  visible,
  courseData,
  isArchived,
  onConfirm,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(
    () => () => {
      if (!visible) {
        setSuccess(false);
        setErrors(null);
      }
    },
    [visible],
  );

  const modalState = useMemo(() => {
    if (success) {
      return 'SUCCESS';
    }

    if (errors) {
      return 'ERRORS';
    }

    if (isArchived) {
      return 'ARCHIVING';
    }

    return 'UNARCHIVING';
  }, [isArchived, success, errors]);

  const modalStep = useMemo(() => modalStepMap[modalState], [modalState]);

  const lastEditedBy = useMemo(() => {
    const diffString = getTimeDiffAsString(courseData.lastEditDate);
    return t('courses:archive_modal.last_edited_by', {
      date: diffString,
      username: courseData.lastEditUsername,
    });
  }, [courseData.lastEditDate, courseData.lastEditUsername]);

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
    } catch (e) {
      setErrors((e as Error).message);
    }
  }, [onConfirm]);

  const handleRequestEnd = useCallback(() => {
    if (errors) {
      return;
    }

    setSuccess(true);
  }, [errors]);

  const hideSuccessModal = useTimeoutCallback(
    useCallback(() => {
      if (success) {
        dispatch(actions.refreshCourseList());
      }
      onClose();
    }, [onClose, success, dispatch]),
    SUCCESS_MODAL_TIMEOUT,
  );

  const handleClose = useCallback(() => {
    const { clear } = hideSuccessModal;

    clear();
    onClose();
  }, [onClose, hideSuccessModal]);

  const handleAutoClose = useCallback(
    (step: string) => {
      if (step === 'SUCCESS') {
        hideSuccessModal.exec();
      }
    },
    [hideSuccessModal],
  );

  return (
    <MModalGroup visible={visible} currentStep={modalStep} onClose={onClose} onStepEnd={handleAutoClose}>
      <MModalGroupItem
        step="ARCHIVE"
        title={t('courses:archive_modal.about_to_archive')}
        labelActionButton={t('courses:archive_modal.archive')}
        labelSecondButton={t('courses:archive_modal.cancel')}
        onAction={handleConfirm}
        onActionEnd={handleRequestEnd}
        onSecondAction={onClose}
        style={styles.modal}
        showExitCross
        type="dialog"
      >
        <div style={styles.publishContainer}>
          <div style={styles.cardContainer}>
            <SCourseCard
              id={courseData.id}
              title={courseData.title}
              language={Enum.Languages[courseData.language as keyof typeof Enum.Languages]}
              status={courseData.status}
              image={courseData.image}
              isStatic
              size="S"
              showStatusIndicator={false}
            />
            <p style={styles.lastEditedBy}>{lastEditedBy}</p>
          </div>

          <div>
            <p style={styles.paragraph}>{t('courses:archive_modal.disclaimer_to_archive_1')}</p>
            <p style={styles.paragraph}>{t('courses:archive_modal.disclaimer_to_archive_2')}</p>
          </div>
        </div>
      </MModalGroupItem>

      <MModalGroupItem
        step="UNARCHIVE"
        title={t('courses:archive_modal.about_to_unarchive')}
        labelActionButton={t('courses:archive_modal.unarchive')}
        actionButtonType="destructive"
        labelSecondButton={t('courses:archive_modal.cancel')}
        onAction={handleConfirm}
        onActionEnd={handleRequestEnd}
        onSecondAction={onClose}
        style={styles.modal}
        showExitCross
        type="dialog"
      >
        <div style={styles.publishContainer}>
          <div style={styles.cardContainer}>
            <SCourseCard
              id={courseData.id}
              title={courseData.title}
              language={Enum.Languages[courseData.language as keyof typeof Enum.Languages]}
              status={courseData.status}
              image={courseData.image}
              isStatic
              size="S"
              showStatusIndicator={false}
            />
            <p style={styles.lastEditedBy}>{lastEditedBy}</p>
          </div>
          <div>
            <p style={styles.paragraph}>{t('courses:archive_modal.disclaimer_to_unarchive_1')}</p>
          </div>
        </div>
      </MModalGroupItem>

      <MModalGroupItem step="SUCCESS" style={styles.modal} onScroll={handleClose} showExitCross type="dialog">
        <div style={styles.successContainer}>
          <h1 style={styles.successTitle}>
            {isArchived
              ? t('courses:archive_modal.successfully_archived')
              : t('courses:archive_modal.successfully_unarchived')}
          </h1>
          <div style={styles.successIllustration}>
            <UIllustration
              color={isArchived ? COLORS.SUCCESS : COLORS.BLUE_GRAY}
              illustration="course-checked"
              data-test-id="success-illustration"
            />
          </div>
        </div>
      </MModalGroupItem>

      <MModalGroupItem step="ERRORS" style={styles.modal} onScroll={handleClose} showExitCross type="dialog">
        <div style={styles.successContainer}>
          <h1 style={styles.errorTitle}>
            {isArchived ? t('courses:archive_modal.cannot_archive') : t('courses:archive_modal.cannot_unarchive')}
          </h1>
          <div style={styles.errorIllustration}>
            <UIllustration illustration="course-error" data-test-id="errors-illustration" />
          </div>
          <div>
            <p style={styles.errorParagraph}>{errors}</p>
          </div>
        </div>
      </MModalGroupItem>
    </MModalGroup>
  );
};

export default CourseArchiveModal;
